import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, NavigationExtras } from '@angular/router';

import * as _ from 'lodash';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
  NodeAssociationEntry,
  NodeChildAssociationEntry,
} from '@alfresco/js-api';

import { Store } from '@ngrx/store';

import { ProcessInstanceInfoModel } from '../models/process-instance-info.model';

import { WkAlfrescoApiService } from '../../../services/wk-alfresco-api.service';

import { DocumentStatus } from '../../../enums';
import { AppConstants } from '../constants/app-constants';

import { CancelProcessDialogComponent } from '../../../dialogs/cancel-process/cancel-process.dialog';
import {
  ReloadDocumentListAction,
  SnackbarErrorAction,
  SnackbarInfoAction,
} from '../../../store/actions/app.custom-actions';

import { AppStore } from '../../../store/states/app.state';
import { WkErrorResponse } from '../../../model/wk-error-response';
import { ProcessListService } from './process-list.service';
import { CommonConstants } from '../../../../app/shared';

@Injectable({
  providedIn: 'root',
})
export class CancelWorkflowService {
  private static readonly CANCEL_PROCESS_SUCCESS_KEY =
    'APP.MESSAGES.INFO.CANCEL_PROCESS_SUCCESS';

  private readonly nonReusableRoutes: string[] = [
    '/process-list',
    '/task-list',
    '/preview',
    '/search',
  ];
  private isNavigating = false;

  constructor(
    private readonly apiService: WkAlfrescoApiService,
    private readonly dialogRef: MatDialog,
    private readonly processListService: ProcessListService,
    private readonly router: Router,
    private readonly store: Store<AppStore>
  ) {}

  public openCancelWorkflowDialog(
    contextAction?: { data?: ProcessInstanceInfoModel | null },
    node?: NodeChildAssociationEntry
  ): void {
    if (node && this.isWorkingCopy(node)) {
      this.showErrorMessage(
        'APP.MESSAGES.ERRORS.CANCEL_PROCESS.DOCUMENT_HAS_WORKING_COPY'
      );
      return;
    }
    const data =
      contextAction?.data || this.processListService.getSelectedTaskEntry?.();
    if (!data) {
      this.confirmCancelProcess({
        id: null as any,
        attachments: [],
        processTemplateName: '',
      } as ProcessInstanceInfoModel);
      return;
    }

    const processIdFromList =
      (data as any)?.processInfo?.processInstanceId || // Task List row (when present)
      (data as any)?.processInstanceId || // sometimes already present
      (data as any)?.id; // Process List row

    // If we don't reliably have a processInstanceId (Task List often only has task id in data.id),
    // fetching the task and continue the existing flow using the resolved process id.
    if (
      !processIdFromList ||
      ((data as any)?.processInfo &&
        !(data as any)?.processInfo?.processInstanceId)
    ) {
      const taskId = (data as any)?.id;
      if (taskId) {
        this.processListService
          .getTaskInfo(taskId)
          .then((t: any) => {
            const newProcessId = t?.processInstanceId;
            const normalizedData = {
              ...data,
              id: newProcessId,
              processInstanceId: newProcessId,
            };

            const attachments = normalizedData.attachments || [];
            const systemDocumentsIds = attachments
              .filter(({ sourceId }) => Boolean(sourceId))
              .map(({ sourceId }) => sourceId);
            if (systemDocumentsIds.length) {
              this.getDocumentsStatus(systemDocumentsIds).subscribe(
                (statuses) => {
                  this.hasAnyWorkingCopyStatus(statuses)
                    ? this.showErrorMessage(
                        CommonConstants.CANCEL_PROCESS_DOCUMENT_HAS_WORKING_COPY
                      )
                    : this.confirmCancelProcess(normalizedData);
                }
              );
            } else {
              this.confirmCancelProcess(normalizedData);
            }
          })
          .catch(() => {
            this.showErrorMessage(
              'Cannot cancel: could not resolve process instance id'
            );
          });
        return;
      }
    }

    (data as any).id = processIdFromList;
    (data as any).processInstanceId = processIdFromList;

    const attachments = data.attachments || [];
    const systemDocumentsIds = attachments
      .filter(({ id }) => Boolean(id))
      .map(({ id }) => id);
    if (systemDocumentsIds.length) {
      this.getDocumentsStatus(systemDocumentsIds).subscribe((statuses) => {
        this.hasAnyWorkingCopyStatus(statuses)
          ? this.showErrorMessage(
              CommonConstants.CANCEL_PROCESS_DOCUMENT_HAS_WORKING_COPY
            )
          : this.confirmCancelProcess(data);
      });
    } else {
      this.confirmCancelProcess(data);
    }
  }

  public isWorkingCopy(node: NodeChildAssociationEntry): boolean {
    return (
      node.entry.aspectNames?.includes(CommonConstants.workingCopyAspect) ||
      node.entry.properties?.[CommonConstants.workingCopyProperty]
    );
  }

  public hasAnyWorkingCopyStatus(statuses: DocumentStatus[]): boolean {
    return _.some(
      statuses,
      (status) => status === DocumentStatus.HasWorkingCopy
    );
  }

  public confirmCancelProcess({
    id: processInstanceId,
    attachments,
    processTemplateName,
  }: ProcessInstanceInfoModel): void {
    const cancelDialogRef = this.dialogRef.open(CancelProcessDialogComponent, {
      data: { processInstanceId, attachments, processTemplateName },
      minWidth: '897px',
      maxWidth: '1000px',
      panelClass: 'cancel-process-dialog',
    });

    cancelDialogRef.afterClosed().subscribe((result) => {
      if (result?.event === 'confirm') {
        const successMessage =
          typeof result.data === 'string' && result.data.trim().length > 0
            ? result.data
            : CancelWorkflowService.CANCEL_PROCESS_SUCCESS_KEY;

        this.store.dispatch(new SnackbarInfoAction(successMessage));
        //Trigger the reload after cancelling workflow
        this.store.dispatch(new ReloadDocumentListAction());
        // If the current route is one of the non-reusable routes, soft reload of the component after cancel workflow
        if (
          this.nonReusableRoutes.some((route) =>
            this.router.url.includes(route)
          )
        ) {
          this.performSoftReload();
        }
      }
    });
  }

  private performSoftReload(): void {
    // Prevent multiple navigations
    if (this.isNavigating) {
      return;
    }
    this.isNavigating = true;

    const currentUrl = this.router.url;
    const baseUrl = currentUrl.split('?')[0].split('#')[0];

    const navigationExtras: NavigationExtras = {
      // Force navigation even if URL is the same
      onSameUrlNavigation: 'reload',
      // Preserve query parameters if any
      queryParamsHandling: 'preserve',
    };

    // Navigate away and back to trigger component reload
    this.router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => {
        return this.router.navigate([baseUrl], navigationExtras);
      })
      .then((navigationResult: boolean) => {
        if (!navigationResult) {
          console.warn('Soft reload navigation failed');
        }
      })
      .catch((error) => {
        console.error('Soft reload navigation error:', error);
      })
      .finally(() => {
        this.isNavigating = false;
      });
  }

  public getDocumentsStatus(
    documentsIds: string[]
  ): Observable<DocumentStatus[]> {
    return forkJoin(
      documentsIds.map((documentId) =>
        this.getWorkingCopy(documentId).pipe(
          map((workingCopy) =>
            workingCopy
              ? DocumentStatus.HasWorkingCopy
              : DocumentStatus.ActualVersion
          ),
          catchError((error) => {
            let errorResponse: WkErrorResponse;
            try {
              errorResponse = JSON.parse(error.message);
            } catch {
              return of(DocumentStatus.Unknown);
            }

            return of(
              errorResponse?.error?.statusCode === 404
                ? DocumentStatus.NotExist
                : DocumentStatus.Unknown
            );
          })
        )
      )
    );
  }

  public showErrorMessage(message: string): void {
    this.store.dispatch(new SnackbarErrorAction(message));
  }

  public getWorkingCopy(
    nodeId: string
  ): Observable<NodeAssociationEntry | null> {
    return from(
      this.apiService.nodesApi.listTargetAssociations(nodeId, {
        where: `(assocType=${AppConstants.WORKING_COPY_LINK})`,
      })
    ).pipe(
      map(({ list: { entries } }) => this.findWorkingCopy(entries) || null)
    );
  }

  private findWorkingCopy(
    entries: NodeAssociationEntry[]
  ): NodeAssociationEntry {
    return _.find(
      entries,
      ({ entry }) =>
        entry.association.assocType === AppConstants.WORKING_COPY_LINK
    );
  }
}
